import { lazy, Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';

const LazyFallback = () => (
    <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <CircularProgress size={32} />
    </Box>
);

const withSuspense = (Component) => (
    <Suspense fallback={<LazyFallback />}>
        <Component />
    </Suspense>
);

// Lazy-loaded page components
const HomeLayout = lazy(() => import("../containers/HomeLayout"));
const MainHome = lazy(() => import("../containers/Home/MainHome"));
const UserSetup = lazy(() => import('../containers/Home/UserSetup'));
const Organizations = lazy(() => import("../containers/Organizations"));
const ServiceAdd = lazy(() => import("../containers/ServiceAdd"));
const Spaces = lazy(() => import("../containers/Spaces/index"));
const AddWorkspace = lazy(() => import("../containers/WorkspaceAdd/index"));
const Projects = lazy(() => import("../containers/Projects/index"));
const AddProject = lazy(() => import("../containers/ProjectAdd/index"));
const SelectActiveProject = lazy(() => import("../containers/SelectActiveProject/index"));

const SettingsHome = lazy(() => import("../containers/Settings/SettingsHome"));
const BilingHome = lazy(() => import("../pages/Billing"));
const UserManagement = lazy(() => import("../pages/UserManagement"));

// const CollectionTypesList = lazy(() => import("../containers/CollectionDefinitions/CollectionTypesList"));
const CollectionTypesList = lazy(() => import("../containers/CollectionDefinitions/CollectionTypeHome"));
const CollectionTypeAdd = lazy(() => import("../containers/CollectionDefinitions/CollectionTypeAdd").then(m => ({ default: m.CollectionTypeAdd })));
const CollectionTypeConfView = lazy(() => import("../containers/CollectionDefinitions/CollectionTypeConfView").then(m => ({ default: m.CollectionTypeConfView })));
const CollectionFieldsetList = lazy(() => import("../containers/CollectionFieldsets/CollectionFieldsetList"));
const CollectionAddFieldSet = lazy(() => import("../containers/CollectionFieldsets/CollectionAddFieldSet").then(m => ({ default: m.CollectionAddFieldSet })));
const CollectionFieldsetConfView = lazy(() => import("../containers/CollectionFieldsets/CollectionFieldsetConfView").then(m => ({ default: m.CollectionFieldsetConfView })));
const CollectionsLayout = lazy(() => import("../containers/CollectionItems/CollectionsLayout").then(m => ({ default: m.CollectionsLayout })));

const FilesListing = lazy(() => import("../containers/MediaFile/FilesListing").then(m => ({ default: m.FilesListing })));
const FilesRecycleBin = lazy(() => import("../containers/MediaFile/FilesRecycleBin").then(m => ({ default: m.FilesRecycleBin })));
const SharedFilesListing = lazy(() => import("../containers/MediaFile/SharedFilesListing").then(m => ({ default: m.SharedFilesListing })));
const FileUpload = lazy(() => import("../containers/MediaFile/FileUpload").then(m => ({ default: m.FileUpload })));

const AppList = lazy(() => import("../containers/Apps/AppList"));
const AppConfig = lazy(() => import("../containers/Apps/AppConfig"));

export const routesConfig = [
    {
        path: '/',
        element: withSuspense(HomeLayout),
        component: 'home'
    },
    {
        path: '/home',
        element: withSuspense(MainHome),
        component: 'home'
    },
    {
        path: '/onboarding',
        element: withSuspense(UserSetup),
        component: 'home'
    },
    {
        path: '/organizations',
        element: withSuspense(Organizations),
        component: 'home'
    },
    {
        path: '/organization/:orgId/services/add-service',
        element: withSuspense(ServiceAdd),
        component: 'home'
    },
    {
        path: '/organization/:orgId/select-active-project',
        element: withSuspense(SelectActiveProject),
        component: 'projects'
    },
    {
        path: '/organization/:orgId/workspaces',
        element: withSuspense(Spaces),
        component: 'spaces',
        requiresActivePlan: true
    },
    {
        path: '/organization/:orgId/workspaces/:action',
        element: withSuspense(AddWorkspace),
        component: 'spaces',
        requiresActivePlan: true
    },
    {
        path: '/organization/:orgId/workspace/:spaceId/projects',
        element: withSuspense(Projects),
        component: 'projects',
        requiresActivePlan: true
    },
    {
        path: '/organization/:orgId/workspace/:spaceId/projects/:action',
        element: withSuspense(AddProject),
        component: 'projects',
        requiresActivePlan: true
    },

    // Collection Definitions
    {
        path: '/workspace/:space_id/project/:project_id/collection-types',
        element: withSuspense(CollectionTypesList),
        component: 'collection_types',
        requiresActivePlan: true
    },
    {
        path: '/workspace/:space_id/project/:project_id/collection-types/add',
        element: withSuspense(CollectionTypeAdd),
        component: 'collection_types',
        requiresActivePlan: true
    },
    {
        path: '/workspace/:space_id/project/:project_id/collection-types/edit',
        element: withSuspense(CollectionTypeAdd),
        component: 'collection_types',
        requiresActivePlan: true
    },
    {
        path: '/workspace/:space_id/project/:project_id/collection-types/add/configure',
        element: withSuspense(CollectionTypeConfView),
        component: 'collection_types',
        requiresActivePlan: true
    },
    {
        path: '/workspace/:space_id/project/:project_id/collection-types/edit/configure',
        element: withSuspense(CollectionTypeConfView),
        component: 'collection_types',
        requiresActivePlan: true
    },

    // Collection Fieldset
    {
        path: '/workspace/:space_id/project/:project_id/field-set',
        element: withSuspense(CollectionFieldsetList),
        component: 'fieldsets',
        requiresActivePlan: true
    },
    {
        path: '/workspace/:space_id/project/:project_id/field-set/add',
        element: withSuspense(CollectionAddFieldSet),
        component: 'fieldsets',
        requiresActivePlan: true
    },
    {
        path: '/workspace/:space_id/project/:project_id/field-set/edit',
        element: withSuspense(CollectionAddFieldSet),
        component: 'fieldsets',
        requiresActivePlan: true
    },
    {
        path: '/workspace/:space_id/project/:project_id/field-set/edit/configure',
        element: withSuspense(CollectionFieldsetConfView),
        component: 'fieldsets',
        requiresActivePlan: true
    },
    {
        path: '/workspace/:space_id/project/:project_id/field-set/add/configure',
        element: withSuspense(CollectionFieldsetConfView),
        component: 'fieldsets',
        requiresActivePlan: true
    },

    // Collection Items
    {
        path: '/workspace/:space_id/project/:project_id/collections',
        element: withSuspense(CollectionsLayout),
        component: 'collections',
        requiresActivePlan: true
    },
    {
        path: '/workspace/:space_id/project/:project_id/collections/:collection_id',
        element: withSuspense(CollectionsLayout),
        component: 'collections',
        requiresActivePlan: true
    },

    // files
    {
        path: '/workspace/:space_id/project/:project_id/files/upload',
        element: withSuspense(FileUpload),
        component: 'files',
        requiresActivePlan: true
    },
    {
        path: '/workspace/:space_id/project/:project_id/files/recyclebin',
        element: withSuspense(FilesRecycleBin),
        component: 'files',
        requiresActivePlan: true
    },
    {
        path: '/workspace/:space_id/project/:project_id/files/shared-with-me',
        element: withSuspense(SharedFilesListing),
        component: 'files',
        requiresActivePlan: true
    },
    {
        path: '/workspace/:space_id/project/:project_id/files/:folder_id/upload',
        element: withSuspense(FileUpload),
        component: 'files',
        requiresActivePlan: true
    },
    {
        path: '/workspace/:space_id/project/:project_id/files',
        element: withSuspense(FilesListing),
        component: 'files',
        requiresActivePlan: true
    },
    {
        path: '/workspace/:space_id/project/:project_id/files/*',
        element: withSuspense(FilesListing),
        component: 'files',
        requiresActivePlan: true
    },

    //Others
    {
        path: '/apps',
        element: withSuspense(AppList),
        component: 'appflyte_apps'
    },

    {
        path: '/apps/:app_id',
        element: withSuspense(AppConfig),
        component: 'appflyte_apps'
    },

    {
        path: '/settings/*',
        element: withSuspense(SettingsHome),
        component: 'settings'
    },
    {
        path: '/user/billing',
        element: withSuspense(BilingHome),
        component: 'Users'
    },
    {
        path: '/user/settings',
        element: withSuspense(UserManagement),
        component: 'Users'
    },

];
